import type { RefObject } from "react";

type Props = {
  value: string;
  onChange: (s: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
};

function wrapInline(
  value: string,
  selStart: number,
  selEnd: number,
  left: string,
  right: string,
  placeholder: string,
) {
  const before = value.slice(0, selStart);
  const selected = value.slice(selStart, selEnd);
  const after = value.slice(selEnd);
  const inner = selected || placeholder;
  const next = `${before}${left}${inner}${right}${after}`;
  const cursorStart = before.length + left.length;
  const cursorEnd = cursorStart + inner.length;
  return { next, cursorStart, cursorEnd };
}

function applyLinePrefix(
  value: string,
  selStart: number,
  selEnd: number,
  prefix: string,
) {
  const lineStart = value.lastIndexOf("\n", selStart - 1) + 1;
  const lineEnd = (() => {
    const idx = value.indexOf("\n", selEnd);
    return idx === -1 ? value.length : idx;
  })();
  const before = value.slice(0, lineStart);
  const block = value.slice(lineStart, lineEnd);
  const after = value.slice(lineEnd);
  const lines = block.split("\n");
  const prefixed = lines
    .map((line, i) => {
      if (prefix === "1. ") return `${i + 1}. ${line}`;
      return `${prefix}${line}`;
    })
    .join("\n");
  const next = `${before}${prefixed}${after}`;
  const cursorStart = lineStart;
  const cursorEnd = before.length + prefixed.length;
  return { next, cursorStart, cursorEnd };
}

export function MarkdownToolbar({ value, onChange, textareaRef }: Props) {
  const apply = (
    fn: (v: string, s: number, e: number) => {
      next: string;
      cursorStart: number;
      cursorEnd: number;
    },
  ) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const { next, cursorStart, cursorEnd } = fn(value, start, end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const handleBold = () =>
    apply((v, s, e) => wrapInline(v, s, e, "**", "**", "текст"));
  const handleItalic = () =>
    apply((v, s, e) => wrapInline(v, s, e, "_", "_", "текст"));
  const handleCode = () =>
    apply((v, s, e) => wrapInline(v, s, e, "`", "`", "код"));
  const handleLink = () => {
    const url = window.prompt("URL ссылки:", "https://");
    if (!url) return;
    apply((v, s, e) => wrapInline(v, s, e, "[", `](${url})`, "ссылка"));
  };
  const handleUL = () => apply((v, s, e) => applyLinePrefix(v, s, e, "- "));
  const handleOL = () => apply((v, s, e) => applyLinePrefix(v, s, e, "1. "));
  const handleH1 = () => apply((v, s, e) => applyLinePrefix(v, s, e, "# "));
  const handleH2 = () => apply((v, s, e) => applyLinePrefix(v, s, e, "## "));

  return (
    <div className="md-toolbar">
      <button type="button" className="md-toolbar-btn md-toolbar-bold"
        onClick={handleBold} title="Жирный" aria-label="Жирный">B</button>
      <button type="button" className="md-toolbar-btn md-toolbar-italic"
        onClick={handleItalic} title="Курсив" aria-label="Курсив">I</button>
      <button type="button" className="md-toolbar-btn md-toolbar-code"
        onClick={handleCode} title="Инлайн-код" aria-label="Инлайн-код">&lt;/&gt;</button>
      <button type="button" className="md-toolbar-btn"
        onClick={handleLink} title="Ссылка" aria-label="Ссылка">🔗</button>
      <span className="md-toolbar-sep" />
      <button type="button" className="md-toolbar-btn"
        onClick={handleUL} title="Список" aria-label="Список">•</button>
      <button type="button" className="md-toolbar-btn"
        onClick={handleOL} title="Нумерованный список" aria-label="Нумерованный список">1.</button>
      <span className="md-toolbar-sep" />
      <button type="button" className="md-toolbar-btn"
        onClick={handleH1} title="Заголовок 1" aria-label="Заголовок 1">H1</button>
      <button type="button" className="md-toolbar-btn"
        onClick={handleH2} title="Заголовок 2" aria-label="Заголовок 2">H2</button>
    </div>
  );
}
